import { useEffect, useMemo } from 'react'
import { FormikProvider } from 'formik'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Form from '@/components/base/form'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { contactRevealAnimation } from '@/components/sections/animations/contact'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { useLocationCascadeFields } from '@/hooks/use-location-cascade-fields'

import { cn } from '@/lib/utils'
import { WPGravityFormField, WPImage, WPPageData } from '@/types'

type StorePageProps = {
  pageProps: WPPageData & {
    featured_image?: WPImage | null
    acf?: WPPageData['acf'] & {
      place?: {
        address?: string
        lat?: number
        lon?: number
      }
      email?: {
        to?: string
        cc?: string
      }
      phone?: {
        phone_1?: string
        phone_2?: string
        phone_3?: string
      }
      opening_hours?: string
    }
  }
}

const StorePage = ({ pageProps }: StorePageProps) => {
  const { strings } = useStore()

  const acf = pageProps.acf
  const form = acf.form || strings?.form
  const formId = useMemo(() => {
    return form?.id
  }, [form])

  const fields = useMemo(
    () => (formId ? form.fields || [] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, FormikProvider]
  )

  const formFields = form?.fields?.map((field: WPGravityFormField) => {
    if (field.type === 'hidden' && field.label === 'email_to') {
      return { ...field, defaultValue: acf?.email?.to || 'web@modulnova.it' }
    }
    if (
      field.type === 'hidden' &&
      field.label === 'email_cc' &&
      acf?.email?.to
    ) {
      return { ...field, defaultValue: acf?.email?.cc }
    }
    return field
  })

  const title = pageProps.title?.rendered ?? ''
  const typology = pageProps?.taxonomies?.['store-typology']?.[0].name

  const mapsHref =
    acf?.place?.lat && acf?.place?.lon
      ? `https://www.google.com/maps/search/?api=1&query=${acf.place.lat},${acf.place.lon}`
      : acf?.place?.address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([title, acf.place.address].filter(Boolean).join(' '))}`
        : null

  const {
    enhancedFields,
    handleFieldChange: handleFormFieldChange,
    selectDependencies,
    resetSelection: resetLocationSelection
  } = useLocationCascadeFields(fields, strings?.labels?.country_region_province)

  useEffect(() => {
    resetLocationSelection()
  }, [resetLocationSelection, strings?.labels?.country_region_province])

  return (
    <Motion animation={contactRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef}>
          <Spacer variant="xxl" />

          <Container>
            <Grid className="gap-y-spacing-2xl">
              {/* Store info */}
              <div
                className="col-span-2 md:col-span-6 lg:col-span-4"
                data-motion-column
              >
                <div
                  className={cn([
                    'flex flex-col rounded-radius-minimal border border-[rgba(255,255,255,0.1)] bg-[#1a1a1a] p-spacing-sm',
                    {
                      'bg-white-light': typology === 'Store'
                    }
                  ])}
                >
                  {strings?.labels.store_send_message ? (
                    <Typography
                      variant="h5"
                      className={cn([
                        'text-text-heading-dark',
                        { 'text-text-heading-light': typology === 'Store' }
                      ])}
                    >
                      {typology === 'Store'
                        ? pageProps.title.rendered
                        : strings.labels.store_send_message}
                    </Typography>
                  ) : null}

                  <Spacer variant="sm" />

                  <div className="flex flex-col gap-y-spacing-xs">
                    <Typography
                      variant="large"
                      className={cn([
                        'text-text-heading-dark',
                        { 'text-text-heading-light': typology === 'Store' }
                      ])}
                    >
                      {typology === 'Store'
                        ? strings?.labels.store_address
                        : pageProps.title.rendered}
                    </Typography>

                    {acf?.place?.address ? (
                      <Typography
                        variant="small"
                        className={cn([
                          'text-text-primary-dark',
                          { 'text-text-primary-light': typology === 'Store' }
                        ])}
                      >
                        {acf.place.address}
                      </Typography>
                    ) : null}
                  </div>

                  {acf?.opening_hours ? (
                    <>
                      <Spacer variant="xs" />

                      <div className="flex flex-col gap-y-spacing-sm">
                        {strings?.labels?.store_hours ? (
                          <Typography
                            variant="large"
                            className={cn([
                              'text-text-heading-dark',
                              {
                                'text-text-heading-light': typology === 'Store'
                              }
                            ])}
                          >
                            {strings.labels.store_hours}
                          </Typography>
                        ) : null}

                        {acf?.opening_hours ? (
                          <Typography
                            variant="small"
                            className={cn([
                              'text-text-primary-dark',
                              {
                                'text-text-primary-light': typology === 'Store'
                              }
                            ])}
                          >
                            {acf.opening_hours}
                          </Typography>
                        ) : null}
                      </div>
                    </>
                  ) : null}

                  <Spacer variant="xs" />

                  <div className="flex flex-col gap-y-spacing-sm">
                    {strings?.labels?.store_contacts ? (
                      <Typography
                        variant="large"
                        className={cn([
                          'text-text-heading-dark',
                          { 'text-text-heading-light': typology === 'Store' }
                        ])}
                      >
                        {strings.labels.store_contacts}
                      </Typography>
                    ) : null}

                    <Typography
                      variant="small"
                      className={cn([
                        'flex flex-col text-text-primary-dark',
                        { 'text-text-primary-light': typology === 'Store' }
                      ])}
                    >
                      {acf?.email?.to ? (
                        <a href={`mailto:${acf.email.to}`}>{acf.email.to}</a>
                      ) : null}
                      <br />
                      {acf?.phone?.phone_1 ? (
                        <a href={`tel:${acf.phone.phone_1}`}>
                          {acf.phone.phone_1}
                        </a>
                      ) : null}
                    </Typography>
                  </div>

                  {strings?.labels.store_view_map && mapsHref ? (
                    <>
                      <Spacer variant="md" />
                      <CtaButton
                        variant={typology === 'Store' ? 'tertiary' : 'primary'}
                        href={mapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {strings.labels.store_view_map}
                      </CtaButton>
                    </>
                  ) : null}
                </div>
              </div>

              <div
                id="store-form"
                className="col-span-2 md:col-span-6 lg:col-span-7 lg:col-start-6"
                data-motion-column
              >
                {formFields && formFields.length > 0 ? (
                  <Form
                    form_id={form.id}
                    fields={enhancedFields}
                    submit={form.button?.text}
                    selectDependencies={selectDependencies}
                    onFieldChange={handleFormFieldChange}
                  />
                ) : null}
              </div>
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default StorePage
